/**
 * My Data Page
 *
 * User data management page with storage usage, file upload, and file browser.
 */

import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Trash2, ArrowLeft } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { StorageUsage, FileUpload, FileBrowser } from '@/components/files';
import { useStorage } from '@/lib/hooks/useStorage';
import { api } from '@/lib/api';
import type { UserFileResponse } from '@/lib/api';

export function MyDataPage() {
  const [searchParams] = useSearchParams();
  const initialFolderId = searchParams.get('folderId') ?? undefined;
  const { refresh: refreshStorage } = useStorage({ autoFetch: true });
  const [activeRefreshTrigger, setActiveRefreshTrigger] = useState(0);
  const [archivedRefreshTrigger, setArchivedRefreshTrigger] = useState(0);
  const [trashRefreshTrigger, setTrashRefreshTrigger] = useState(0);
  const [trashFolderId, setTrashFolderId] = useState<string | undefined>(undefined);
  const [showTrash, setShowTrash] = useState(false);
  const [currentBrowseFolderId, setCurrentBrowseFolderId] = useState<string | undefined>(
    initialFolderId,
  );

  const handleUploadComplete = useCallback(
    (files: UserFileResponse[]) => {
      // Refresh both storage and active file list after upload
      refreshStorage();
      setActiveRefreshTrigger((prev) => prev + 1);
      console.log(`Uploaded ${files.length} file(s)`);
    },
    [refreshStorage],
  );

  const handleFilesDeleted = useCallback(() => {
    // Refresh storage and trash tab after deletion (files move to trash)
    refreshStorage();
    setTrashRefreshTrigger((prev) => prev + 1);
  }, [refreshStorage]);

  const handleFileArchived = useCallback(() => {
    // Refresh both tabs when a file is archived
    refreshStorage();
    setActiveRefreshTrigger((prev) => prev + 1);
    setArchivedRefreshTrigger((prev) => prev + 1);
  }, [refreshStorage]);

  const handleFileRestored = useCallback(() => {
    // Refresh both tabs when a file is restored
    refreshStorage();
    setActiveRefreshTrigger((prev) => prev + 1);
    setArchivedRefreshTrigger((prev) => prev + 1);
  }, [refreshStorage]);

  const handleFilesDeleted2 = useCallback(() => {
    refreshStorage();
    setTrashRefreshTrigger((prev) => prev + 1);
  }, [refreshStorage]);

  const handleRestoredFromTrash = useCallback(() => {
    refreshStorage();
    setActiveRefreshTrigger((prev) => prev + 1);
    setTrashRefreshTrigger((prev) => prev + 1);
  }, [refreshStorage]);

  const handlePermanentlyDeleted = useCallback(() => {
    refreshStorage();
    setTrashRefreshTrigger((prev) => prev + 1);
  }, [refreshStorage]);

  const handleOpenTrash = useCallback(async () => {
    let folderId = trashFolderId;
    if (!folderId) {
      const { folder } = await api.ensureTrashFolder();
      folderId = folder.folderId;
      setTrashFolderId(folderId);
      // Bump active refresh so the folder list picks up the new Trash folder
      setActiveRefreshTrigger((prev) => prev + 1);
    }
    setShowTrash(true);
  }, [trashFolderId]);

  return (
    <div>
      {/* Page Header */}
      <div className="border-b border-border bg-card">
        <div className="max-w-6xl mx-auto px-4 py-4 sm:px-6 lg:px-8">
          <h1 className="text-2xl font-bold text-foreground">My Data</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your files and storage</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8 sm:px-6 lg:px-8 space-y-6">
        {/* Storage Usage */}
        <StorageUsage showBreakdown={true} />

        {/* File Upload */}
        <Card>
          <CardHeader>
            <CardTitle>Upload Files</CardTitle>
          </CardHeader>
          <CardContent>
            <FileUpload
              onUploadComplete={handleUploadComplete}
              maxFiles={10}
              maxSize={500 * 1024 * 1024}
              folderId={currentBrowseFolderId}
            />
          </CardContent>
        </Card>

        {/* File Browser */}
        <Card>
          <CardContent className="pt-6">
            {showTrash ? (
              <>
                <div className="flex items-center gap-3 mb-4">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowTrash(false)}
                    className="gap-1.5"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back to Files
                  </Button>
                  <h3 className="text-sm font-medium flex items-center gap-1.5">
                    <Trash2 className="h-4 w-4" />
                    Trash
                  </h3>
                </div>
                <p className="text-sm text-muted-foreground mb-4">
                  Deleted files are kept here for 7 days before being permanently removed. Restore
                  files to move them back, or delete permanently to free up space immediately.
                </p>
                {trashFolderId ? (
                  <FileBrowser
                    status="active"
                    initialFolderId={trashFolderId}
                    onFilesDeleted={handleFilesDeleted2}
                    onFileRestoredFromTrash={handleRestoredFromTrash}
                    onFilePermanentlyDeleted={handlePermanentlyDeleted}
                    multiSelect={true}
                    className="min-h-[400px]"
                    refreshTrigger={trashRefreshTrigger}
                  />
                ) : (
                  <div className="flex items-center justify-center min-h-[400px] text-muted-foreground text-sm">
                    Trash is empty
                  </div>
                )}
              </>
            ) : (
              <Tabs defaultValue="active">
                <div className="flex items-center justify-between">
                  <TabsList>
                    <TabsTrigger value="active">Active Files</TabsTrigger>
                    <TabsTrigger value="archived">Archived</TabsTrigger>
                  </TabsList>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleOpenTrash}
                    className="gap-1.5 text-muted-foreground hover:text-foreground"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span className="hidden sm:inline">Trash</span>
                  </Button>
                </div>
                <TabsContent value="active" forceMount className="data-[state=inactive]:hidden">
                  <p className="text-sm text-muted-foreground mb-4">
                    Files available for immediate access. Download and organize your files (or
                    archive files you don't need access to right now).
                  </p>
                  <FileBrowser
                    status="active"
                    initialFolderId={initialFolderId}
                    onFilesDeleted={handleFilesDeleted}
                    onFileArchived={handleFileArchived}
                    onFolderChange={setCurrentBrowseFolderId}
                    multiSelect={true}
                    className="min-h-[400px]"
                    refreshTrigger={activeRefreshTrigger}
                  />
                </TabsContent>
                <TabsContent value="archived" forceMount className="data-[state=inactive]:hidden">
                  <p className="text-sm text-muted-foreground mb-4">
                    Long-term storage at reduced cost. Restore files when you need them again.
                  </p>
                  <FileBrowser
                    status="archived"
                    onFilesDeleted={handleFilesDeleted}
                    onFileRestored={handleFileRestored}
                    multiSelect={true}
                    className="min-h-[400px]"
                    refreshTrigger={archivedRefreshTrigger}
                  />
                </TabsContent>
              </Tabs>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

export default MyDataPage;
